/* eslint-disable jsx-a11y/control-has-associated-label,react-hooks/exhaustive-deps */
import React, { useState, useMemo, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import ModalBodyLayout from '../partials/MainBodyLayout';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import {
  CARD_NETWORKS,
  cardOrderFeesKeys,
  cardProviders,
  getCardCapabilities,
  orderCardFlowScreens,
  shouldOrderWithoutPayment,
  TermsSettingsUrls,
  WalletsTypes,
} from '../../../constants';
import {
  CARD_CONNECT_FINANCIAL_ORDER,
  CARD_ORDER_MONAVATE,
  USA_CARD_CARD_ORDER,
} from '../../../mutations';
import { showAlert } from '../../../../../utilities/notifications';
import TermsCheckbox from '../../../../../components/TermsCheckbox';
import SupportModal from '../../../../Accounts/modals/SupportModal';
import { FLOWS, sendStats } from '../../../../../services/mixpanel';
import {
  MXP_CARD_ORDER_CONFIRM_DETAILS,
  MXP_CARD_ORDER_CONFIRM_DETAILS_VISIT,
} from '../../mixpanel-events';
import { Button, Typography } from '../../../../../components';
import {
  formatMonovateCardHolderName,
  useUserPersonalInfo,
} from '../../../../../utilities/sharedHooks/user';
import MastercardLogo from '../../../../../assets/images/mastercard.svg';
import VisaLogo from '../../../../../assets/images/visa.svg';
import { capitalizeFirst } from '../../../../../utilities/helpers';
import VisaUSLogo from '../../../../../assets/images/visa-logo.svg';

const ConfirmVirtualDetails = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();

  const [selectedTrouble, setSelectedTrouble] = useState(false);
  const [termAndConditionChecked, setTermAndConditionChecked] = useState(false);

  const { state, openScreen } = useContext(OrderCardContext);
  const cardNetwork = state?.cardNetwork;
  const cardType = state?.cardType;
  const { isUS, isLoading: isLoadingUser } = useUserPersonalInfo();

  const screenOptions = state?.screenOptions;

  useEffect(() => {
    sendStats(
      MXP_CARD_ORDER_CONFIRM_DETAILS_VISIT,
      {
        mainCardType: state?.mainCardType,
        cardProvider: state?.cardType,
        cardCurrency: state?.cardCurrency?.coin?.toUpperCase(),
      },
      FLOWS.CARD_ORDER
    );
  }, []);

  const uiFeatureShowHint = state?.cardOrderSettings?.ui?.ui_feature_show_hints;
  const ledgerSupportEmailAddress = state?.cardOrderSettings?.server?.support_email_address;
  const virtualCardOrderFee = state?.cardOrderSettings?.server?.[cardOrderFeesKeys.ROW];
  const userFirstName = state?.cardHolderName?.firstName;
  const userLastName = state?.cardHolderName?.lastName;

  const defaultWalletForUsingWhenFeeZero = useMemo(() => {
    if (state?.userWallets) {
      return state?.userWallets?.find((w) => w.wallet_type === WalletsTypes.CRYPTO);
    }
    return {};
  }, [state?.userWallets]);

  const handleClick = () => {
    if (uiFeatureShowHint) {
      history.push('/hint/card');
    }
  };

  const closeCardOrder = () => history.push('/dashboard/card');

  const { mutate: orderCardMonavate, isLoading: isLoadingVirtualMonavate } = useMutation(
    CARD_ORDER_MONAVATE,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data && data.success) {
          showAlert({
            message: translate('mastercard_virtual_request_success'),
            type: 'success',
            blockAutoDismiss: true,
            showIcon: true,
          });
        } else {
          showAlert({
            message: translate('card_order_error_msg').replace(
              '__SUPPORT_EMAIL__',
              ledgerSupportEmailAddress
            ),
            type: 'danger',
          });
        }
        closeCardOrder();
      },
    }
  );

  const { mutate: orderCardGalileo, isLoading: isLoadingGalileo } = useMutation(
    USA_CARD_CARD_ORDER,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data && data.success) {
          showAlert({
            message: translate('us_virtual_card_request_success'),
            type: 'success',
            blockAutoDismiss: true,
            showIcon: true,
            onClick: handleClick,
          });
        } else {
          showAlert({
            message: translate('card_order_error_msg').replace(
              '__SUPPORT_EMAIL__',
              ledgerSupportEmailAddress
            ),
            type: 'danger',
          });
        }
        // special redirect only for galileo card order COB-508
        history.push('/dashboard/card/details', { galileoCardUnderPreparing: true });
      },
    }
  );

  const { mutate: orderCardConnectFinancial, isLoading: isLoadingConnectFinancial } = useMutation(
    CARD_CONNECT_FINANCIAL_ORDER,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
          closeCardOrder();
          return;
        }
        if (!data?.success) {
          showAlert({
            message: translate('card_order_error_msg').replace(
              '__SUPPORT_EMAIL__',
              ledgerSupportEmailAddress
            ),
            type: 'danger',
          });
          closeCardOrder();
          return;
        }
        const isIssued = data.cardOrderStatus === 'issued';
        showAlert({
          message: translate(
            isIssued ? 'us_virtual_card_request_success' : 'card_connect_financial_order_pending'
          ),
          type: 'success',
          blockAutoDismiss: true,
          showIcon: true,
        });
        history.push(isIssued ? '/dashboard/card/details' : '/dashboard/card');
      },
    }
  );

  const handleOnConfirm = () => {
    sendStats(
      MXP_CARD_ORDER_CONFIRM_DETAILS,
      {
        mainCardType: state?.mainCardType,
        cardProvider: state?.cardType,
        cardCurrency: state?.cardCurrency?.coin?.toUpperCase(),
      },
      FLOWS.CARD_ORDER
    );
    if (shouldOrderWithoutPayment(state?.cardType, virtualCardOrderFee)) {
      switch (state?.cardType) {
        case cardProviders.MONAVATE:
          orderCardMonavate({
            platform: state.cardCurrency.coin,
            currency: defaultWalletForUsingWhenFeeZero?.coin?.toUpperCase(),
            isVirtual: true,
          });
          break;
        case cardProviders.GALILEO:
          orderCardGalileo({
            platform: state.cardCurrency.coin,
            currency: defaultWalletForUsingWhenFeeZero?.coin?.toUpperCase(),
            isVirtual: true,
          });
          break;
        case cardProviders.CONNECT_FINANCIAL:
          orderCardConnectFinancial();
          break;
        default:
          break;
      }
    } else {
      openScreen(orderCardFlowScreens.SELECT_PAYMENT_TYPE);
    }
  };

  const renderFooter = () => (
    <>
      <div className="mt-5 mb-5">
        <TermsCheckbox
          contentUrlKey={
            cardType === cardProviders.CONNECT_FINANCIAL && !isLoadingUser && isUS === false
              ? TermsSettingsUrls.CARD_CONNECT_FINANCIAL_ROW_TERMS_CONTENT_URL
              : TermsSettingsUrls.CARD_MONAVATE_TERMS_CONTENT_URL
          }
          checked={termAndConditionChecked}
          onChange={() => setTermAndConditionChecked((prev) => !prev)}
        />
        {selectedTrouble && <SupportModal hide={() => setSelectedTrouble((prev) => !prev)} />}
      </div>
      <Button
        variant="primary"
        className="w-100"
        onClick={handleOnConfirm}
        disabled={
          !termAndConditionChecked ||
          isLoadingVirtualMonavate ||
          isLoadingGalileo ||
          isLoadingConnectFinancial
        }>
        {translate('order_card')}
      </Button>
    </>
  );

  const cardLogo = useMemo(() => {
    if (getCardCapabilities(cardType).usCardArtwork && cardNetwork === CARD_NETWORKS.VISA) {
      return VisaUSLogo;
    }
    if (cardNetwork === CARD_NETWORKS.VISA) {
      return VisaLogo;
    }
    return MastercardLogo;
  }, [cardType, cardNetwork]);

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={
        screenOptions?.onlyVirtualCardAllowed
          ? closeCardOrder
          : () => openScreen(orderCardFlowScreens.SELECT_CARD_TYPE)
      }
      title={translate('card_order_confirm_details_title')}>
      <div className="mt-4 pt-3">
        <div>
          <Typography variant="base" className="text-dark">
            {translate('virtual_card_order_confirm_details_subtitle')}
          </Typography>
        </div>
        <div
          onClick={() => setSelectedTrouble((prev) => !prev)}
          aria-hidden="true"
          className="mt-4 mb-2 cursor-pointer">
          <Typography variant="baseBold" className="text-primary">
            {translate('card_order_confirm_name_having_trouble')}
          </Typography>
        </div>
        <div className="bg-lighter rounded-extra p-3 mt-3">
          {state?.cardType === cardProviders.MONAVATE ? (
            <div className="mb-4">
              <Typography variant="small" className="text-shadow">
                {translate('card_order_confirm_details_cardholder_name')}
              </Typography>
              <Typography variant="base" className="text-dark">
                {formatMonovateCardHolderName(userFirstName, userLastName)}
              </Typography>
            </div>
          ) : null}
          <div className="mb-4">
            <Typography variant="small" className="text-shadow">
              {translate('card_order_confirm_details_full_name')}
            </Typography>
            <Typography variant="base" className="text-dark">
              {`${userFirstName} ${userLastName}`}
            </Typography>
          </div>
          <div className="mb-4">
            <Typography variant="small" className="text-shadow">
              {translate('card_order_confirm_details_card_currency')}
            </Typography>
            <div className="d-flex flex-row justify-content-between align-items-center">
              <Typography variant="base" className="text-dark">
                {state?.cardCurrency?.coin?.toUpperCase()}
              </Typography>
            </div>
          </div>
          <div>
            <Typography variant="small" className="text-shadow">
              {translate('card_order_confirm_details_card_type')}
            </Typography>
            <div className="d-flex flex-row justify-content-between align-items-center">
              <Typography variant="base" className="text-dark">
                {translate('card_order_confirm_details_mastercard_virtual_label', {
                  network: capitalizeFirst(cardNetwork?.toLowerCase() || ''),
                })}
              </Typography>
              <div style={{ width: '2rem' }} className="d-flex justify-content-end">
                <img src={cardLogo} alt="card provider logo" />
              </div>
            </div>
          </div>
        </div>
        {renderFooter()}
      </div>
    </ModalBodyLayout>
  );
};

export default ConfirmVirtualDetails;
